import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  ArrowLeft, ChevronRight, Plus, RefreshCw, Trash2,
  CheckCircle2, AlertCircle, Clock, Search, Move,
  ShieldCheck, ShieldOff, Folder, FolderOpen,
} from 'lucide-react'
import clsx from 'clsx'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { adApi } from '../../services/adApi'
import { useAdConfigs } from '../../hooks/useAdDomains'
import { formatDateTime } from '../../utils/format'

const SYNC_STATUS = {
  Synced:   { key: 'synced',   cls: 'text-green-600 bg-green-50',  Icon: CheckCircle2 },
  Orphaned: { key: 'orphaned', cls: 'text-red-600 bg-red-50',      Icon: AlertCircle  },
  Error:    { key: 'error',    cls: 'text-orange-600 bg-orange-50',Icon: Clock        },
}

// ─── Seletor de OU (árvore) ───────────────────────────────────────────────────
function OuSelector({ configId, selected, onSelect }) {
  const { t } = useTranslation()
  const { data: tree, isLoading } = useQuery({
    queryKey: ['ou-tree', configId],
    queryFn: () => adApi.getOuTree(configId),
    enabled: !!configId,
  })

  function OuNode({ node, depth = 0 }) {
    const [open, setOpen] = useState(depth === 0)
    const hasChildren = node.children?.length > 0
    const isSelected = selected === node.dn

    return (
      <div>
        <div
          className={clsx(
            'flex items-center gap-2 px-2 py-1.5 rounded-lg cursor-pointer text-sm transition-colors',
            isSelected ? 'bg-blue-100 text-blue-800' : 'hover:bg-gray-100 text-gray-700',
          )}
          style={{ paddingLeft: `${8 + depth * 16}px` }}
          onClick={() => onSelect(node.dn)}
        >
          {hasChildren ? (
            <button type="button" onClick={(e) => { e.stopPropagation(); setOpen(!open) }}>
              {open ? <FolderOpen className="w-4 h-4 text-yellow-500" /> : <Folder className="w-4 h-4 text-yellow-500" />}
            </button>
          ) : (
            <Folder className="w-4 h-4 text-gray-400 flex-shrink-0" />
          )}
          <span className="truncate">{node.name}</span>
          {isSelected && <CheckCircle2 className="w-3.5 h-3.5 text-blue-600 ml-auto" />}
        </div>
        {open && hasChildren && node.children.map((child) => (
          <OuNode key={child.dn} node={child} depth={depth + 1} />
        ))}
      </div>
    )
  }

  if (isLoading) return <div className="text-sm text-gray-400 p-3">{t('domains.managedGroups.loadingOus')}</div>
  if (!tree?.length) return <div className="text-sm text-gray-400 p-3">{t('domains.managedGroups.ouEmpty')}</div>

  return (
    <div className="border border-gray-200 rounded-xl bg-gray-50 max-h-64 overflow-y-auto p-2">
      {tree.map((node) => <OuNode key={node.dn} node={node} />)}
    </div>
  )
}

// ─── Modal adotar grupo ───────────────────────────────────────────────────────
function AdoptGroupModal({ configId, onAdopt, onClose }) {
  const { t } = useTranslation()
  const [query, setQuery] = useState('')
  const [results, setResults] = useState(null)
  const [loading, setLoading] = useState(false)

  const handleSearch = async (e) => {
    e.preventDefault()
    setLoading(true)
    try {
      const data = await adApi.searchGroups(configId, query)
      setResults(data)
    } catch (err) {
      alert(t('domains.managedGroups.genericError') + err.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-gray-900">{t('domains.managedGroups.adoptTitle')}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
        </div>
        <p className="text-sm text-gray-500">
          {t('domains.managedGroups.adoptHint')}
        </p>
        <form onSubmit={handleSearch} className="flex gap-2">
          <input
            type="text" value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder={t('domains.managedGroups.adoptSearchPlaceholder')} className="input flex-1 text-sm"
          />
          <button type="submit" disabled={loading} className="btn btn-secondary text-sm px-3">
            {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          </button>
        </form>
        <div className="max-h-64 overflow-y-auto divide-y divide-gray-50">
          {results?.length === 0 && <div className="py-4 text-center text-sm text-gray-400">{t('domains.managedGroups.adoptNoResults')}</div>}
          {results?.map((g) => (
            <div
              key={g.dn}
              className="flex items-center justify-between p-3 hover:bg-blue-50 cursor-pointer"
              onClick={() => onAdopt(g)}
            >
              <div>
                <div className="text-sm font-medium text-gray-800">{g.displayName}</div>
                <div className="text-xs text-gray-400 font-mono truncate">{g.dn}</div>
              </div>
              <ShieldCheck className="w-4 h-4 text-blue-500 flex-shrink-0" />
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// ─── Modal criar grupo ────────────────────────────────────────────────────────
function CreateGroupModal({ configId, onClose, onCreated }) {
  const { t } = useTranslation()
  const [form, setForm] = useState({ groupName: '', description: '', groupType: 'GlobalSecurity' })
  const [selectedOu, setSelectedOu] = useState(null)
  const [loading, setLoading] = useState(false)

  const handleCreate = async (e) => {
    e.preventDefault()
    if (!selectedOu) { alert(t('domains.managedGroups.selectOu')); return }
    setLoading(true)
    try {
      await onCreated({ ...form, parentDn: selectedOu })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-gray-900">{t('domains.managedGroups.createTitle')}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
        </div>
        <form onSubmit={handleCreate} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('domains.managedGroups.createNameLabel')}</label>
            <input type="text" value={form.groupName} onChange={(e) => setForm({ ...form, groupName: e.target.value })} className="input w-full" required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('domains.managedGroups.createDescLabel')}</label>
            <input type="text" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="input w-full" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('domains.managedGroups.createTypeLabel')}</label>
            <select value={form.groupType} onChange={(e) => setForm({ ...form, groupType: e.target.value })} className="input w-full">
              <option value="GlobalSecurity">Global Security</option>
              <option value="DomainLocalSecurity">Domain Local Security</option>
              <option value="UniversalSecurity">Universal Security</option>
              <option value="GlobalDistribution">Global Distribution</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('domains.managedGroups.createOuLabel')}</label>
            <OuSelector configId={configId} selected={selectedOu} onSelect={setSelectedOu} />
            {selectedOu && <div className="text-xs text-gray-500 font-mono mt-1">{selectedOu}</div>}
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn btn-secondary flex-1">{t('common.buttons.cancel')}</button>
            <button type="submit" disabled={loading || !form.groupName || !selectedOu} className="btn btn-primary flex-1 flex items-center justify-center gap-2">
              {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              {t('domains.managedGroups.create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ─── Modal mover grupo ────────────────────────────────────────────────────────
function MoveGroupModal({ configId, group, onClose, onMove }) {
  const { t } = useTranslation()
  const [selectedOu, setSelectedOu] = useState(null)
  const [loading, setLoading] = useState(false)

  const handleMove = async () => {
    if (!selectedOu) { alert(t('domains.managedGroups.selectOu')); return }
    setLoading(true)
    try { await onMove(group.id, selectedOu) }
    finally { setLoading(false) }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-gray-900">{t('domains.managedGroups.moveTitle')}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
        </div>
        <p className="text-sm text-gray-600">
          {t('domains.managedGroups.moveLabel')} <span className="font-medium">{group.displayName}</span>
        </p>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">{t('domains.managedGroups.moveOuLabel')}</label>
          <OuSelector configId={configId} selected={selectedOu} onSelect={setSelectedOu} />
          {selectedOu && <div className="text-xs text-gray-500 font-mono mt-1">{selectedOu}</div>}
        </div>
        <div className="flex gap-3">
          <button onClick={onClose} className="btn btn-secondary flex-1">{t('common.buttons.cancel')}</button>
          <button onClick={handleMove} disabled={loading || !selectedOu} className="btn btn-primary flex-1 flex items-center justify-center gap-2">
            {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Move className="w-4 h-4" />}
            {t('domains.managedGroups.moveSubmit')}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Página principal ─────────────────────────────────────────────────────────
export default function ManagedGroups() {
  const { t } = useTranslation()
  const { configId } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { data: configs } = useAdConfigs()
  const domain = configs?.find((c) => c.id === configId)

  const { data: groups, isLoading } = useQuery({
    queryKey: ['managed-groups', configId],
    queryFn: () => adApi.getManagedGroups(configId),
    enabled: !!configId,
  })

  const [showAdopt, setShowAdopt] = useState(false)
  const [showCreate, setShowCreate] = useState(false)
  const [moveTarget, setMoveTarget] = useState(null)

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['managed-groups', configId] })

  const handleAdopt = async (group) => {
    try {
      await adApi.adoptGroup(configId, group.dn)
      setShowAdopt(false)
      invalidate()
    } catch (e) {
      alert(t('domains.managedGroups.adoptError') + (e.response?.data?.error ?? e.message))
    }
  }

  const handleCreate = async (data) => {
    try {
      await adApi.createManagedGroup(configId, data)
      setShowCreate(false)
      invalidate()
    } catch (e) {
      alert(t('domains.managedGroups.createError') + (e.response?.data?.error ?? e.message))
    }
  }

  const handleMove = async (managedGroupId, targetOuDn) => {
    try {
      await adApi.moveGroup(configId, managedGroupId, targetOuDn)
      setMoveTarget(null)
      invalidate()
    } catch (e) {
      alert(t('domains.managedGroups.moveError') + (e.response?.data?.error ?? e.message))
    }
  }

  const handleSync = async (managedGroupId) => {
    try {
      await adApi.syncGroup(configId, managedGroupId)
      invalidate()
    } catch (e) {
      alert(t('domains.managedGroups.syncError') + e.message)
    }
  }

  const handleUnmanage = async (managedGroupId, name) => {
    if (!window.confirm(t('domains.managedGroups.unmanagePrompt', { name }))) return
    try {
      await adApi.unmanageGroup(configId, managedGroupId)
      invalidate()
    } catch (e) {
      alert(t('domains.managedGroups.genericError') + e.message)
    }
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(`/domains/${configId}`)} className="btn btn-secondary text-sm flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" /> {t('common.buttons.back')}
        </button>
        <ChevronRight className="w-4 h-4 text-gray-400" />
        <span className="text-sm text-gray-500 cursor-pointer hover:underline" onClick={() => navigate('/domains')}>{t('domains.title')}</span>
        <ChevronRight className="w-4 h-4 text-gray-400" />
        <span className="text-sm text-gray-500 cursor-pointer hover:underline" onClick={() => navigate(`/domains/${configId}`)}>{domain?.displayName}</span>
        <ChevronRight className="w-4 h-4 text-gray-400" />
        <span className="text-sm font-medium text-gray-800">{t('domains.managedGroups.title')}</span>
      </div>

      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('domains.managedGroups.title')}</h1>
          <p className="text-sm text-gray-500 mt-1">
            {t('domains.managedGroups.pageSubtitle')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowAdopt(true)} className="btn btn-secondary text-sm flex items-center gap-1">
            <ShieldCheck className="w-4 h-4" />
            {t('domains.managedGroups.adopt')}
          </button>
          <button onClick={() => setShowCreate(true)} className="btn btn-primary text-sm flex items-center gap-1">
            <Plus className="w-4 h-4" />
            {t('domains.managedGroups.create')}
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="text-center py-10 text-gray-400">{t('common.generic.loading')}</div>
      ) : !groups?.length ? (
        <div className="card p-12 text-center">
          <ShieldOff className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="font-medium text-gray-700 mb-2">{t('domains.managedGroups.emptyTitle')}</h3>
          <p className="text-sm text-gray-500 mb-6">{t('domains.managedGroups.emptyHint')}</p>
          <div className="flex gap-3 justify-center">
            <button onClick={() => setShowAdopt(true)} className="btn btn-secondary text-sm">
              <ShieldCheck className="w-4 h-4 mr-1" /> {t('domains.managedGroups.adopt')}
            </button>
            <button onClick={() => setShowCreate(true)} className="btn btn-primary text-sm">
              <Plus className="w-4 h-4 mr-1" /> {t('domains.managedGroups.create')}
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {groups.map((g) => {
            const sync = SYNC_STATUS[g.syncStatus] ?? SYNC_STATUS.Error
            const SyncIcon = sync.Icon
            return (
              <div key={g.id} className="card p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-sm font-semibold text-gray-900">{g.displayName}</h3>
                      <span className={clsx('flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium', sync.cls)}>
                        <SyncIcon className="w-3 h-3" />
                        {t(`domains.managedGroups.syncStatus.${sync.key}`)}
                      </span>
                      {g.createdBySystem ? (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">{t('domains.managedGroups.badgeCreatedBySystem')}</span>
                      ) : (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{t('domains.managedGroups.badgeAdopted')}</span>
                      )}
                    </div>
                    <div className="text-xs text-gray-400 font-mono mt-1 truncate">{g.distinguishedName}</div>
                    {g.description && <div className="text-xs text-gray-500 mt-0.5">{g.description}</div>}
                    {g.lastSyncAt && (
                      <div className="text-xs text-gray-400 mt-0.5">
                        {t('domains.managedGroups.syncLabel')} {formatDateTime(g.lastSyncAt)}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={() => handleSync(g.id)}
                      className="btn btn-secondary text-xs p-2"
                      title={t('domains.managedGroups.actionSync')}
                    >
                      <RefreshCw className="w-3.5 h-3.5" />
                    </button>
                    {g.createdBySystem && (
                      <button
                        onClick={() => setMoveTarget(g)}
                        className="btn btn-secondary text-xs p-2"
                        title={t('domains.managedGroups.actionMove')}
                      >
                        <Move className="w-3.5 h-3.5" />
                      </button>
                    )}
                    <button
                      onClick={() => handleUnmanage(g.id, g.displayName)}
                      className="btn btn-secondary text-xs p-2 text-red-500 hover:bg-red-50"
                      title={t('domains.managedGroups.actionUnmanage')}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {showAdopt && (
        <AdoptGroupModal
          configId={configId}
          onAdopt={handleAdopt}
          onClose={() => setShowAdopt(false)}
        />
      )}

      {showCreate && (
        <CreateGroupModal
          configId={configId}
          onClose={() => setShowCreate(false)}
          onCreated={handleCreate}
        />
      )}

      {moveTarget && (
        <MoveGroupModal
          configId={configId}
          group={moveTarget}
          onClose={() => setMoveTarget(null)}
          onMove={handleMove}
        />
      )}
    </div>
  )
}
